import { SeeqNames } from '@seeqdev/constants';

export type PublicationProvenance = {
  sourceScalingTableId: string;
  selectedOutputColumnId: string;
  sourceRowItemId: string;
  sourceRowDatumId?: string;
};

export type PublicationProvenanceData = {
  publicationProvenance: PublicationProvenance;
  eventOverrideColumnIds: string[];
};

const isRecord = (value: unknown): value is Record<string, unknown> => {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
};

const isNonEmptyString = (value: unknown): value is string => {
  return typeof value === 'string' && value.trim().length > 0;
};

const normalizeColumnName = (columnName: string): string => columnName.trim().toLowerCase();

/** Avoids replacing a grandfathered persisted column with the reserved virtual projection. */
export const shouldRequestPublicationProvenance = (
  isItemTable: boolean,
  columns: readonly { columnName: string }[],
): boolean =>
  isItemTable &&
  !columns.some(
    ({ columnName }) =>
      normalizeColumnName(columnName) === normalizeColumnName(SeeqNames.MaterializedTables.PublicationProvenanceColumn),
  );

/** Identifies the server-computed projection without hiding a persisted column with the same name. */
export const isPublicationProvenanceProjectionHeader = (header: { id?: string; name?: string }): boolean =>
  header.id == null && header.name === SeeqNames.MaterializedTables.PublicationProvenanceColumn;

/** Parses the server projection needed to annotate Event Ancestor cells. */
export const parsePublicationProvenance = (value: unknown): PublicationProvenanceData | undefined => {
  if (typeof value !== 'string') {
    return undefined;
  }

  try {
    const parsedProjection = JSON.parse(value) as unknown;
    if (!isRecord(parsedProjection) || !isRecord(parsedProjection.publicationProvenance)) {
      return undefined;
    }

    const provenance = parsedProjection.publicationProvenance;
    if (
      !isNonEmptyString(provenance.sourceScalingTableId) ||
      !isNonEmptyString(provenance.selectedOutputColumnId) ||
      !isNonEmptyString(provenance.sourceRowItemId) ||
      (provenance.sourceRowDatumId !== undefined && typeof provenance.sourceRowDatumId !== 'string') ||
      !Array.isArray(parsedProjection.eventOverrideColumnIds) ||
      !parsedProjection.eventOverrideColumnIds.every(isNonEmptyString)
    ) {
      return undefined;
    }

    const publicationProvenance: PublicationProvenance = {
      sourceScalingTableId: provenance.sourceScalingTableId,
      selectedOutputColumnId: provenance.selectedOutputColumnId,
      sourceRowItemId: provenance.sourceRowItemId,
    };
    if (typeof provenance.sourceRowDatumId === 'string') {
      publicationProvenance.sourceRowDatumId = provenance.sourceRowDatumId;
    }

    return {
      publicationProvenance,
      eventOverrideColumnIds: parsedProjection.eventOverrideColumnIds,
    };
  } catch {
    return undefined;
  }
};
